import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add,
  Business as BusinessIcon,
  DeleteOutline,
  ExpandMore,
  Person as PersonIcon,
} from '@mui/icons-material';
import { IPagination } from '../Interfaces/IPagination';
import { ILegalClient } from '../Interfaces/ILegalClient';
import { useGetLegalClients } from '../hooks/reactQuerys/LegalClientQueries';
import { ID_LEGAL_CLIENT_ASSET_ROLE_OWNER } from '../hooks/reactQuerys/AssetQueries';
import { IBlattOwner } from '../hooks/reactQuerys/GrundbuchblattQueries';

interface IGrundstueckOption {
  id: number;
  lfdNr: string;
}

interface IProps {
  owners: IBlattOwner[];
  setOwners: (owners: IBlattOwner[]) => void;
  /** Grundstücke des Blatts für die Referenz-Auswahl (lfd. Nr.). */
  grundstueckOptions: IGrundstueckOption[];
}

const clientName = (o: IBlattOwner): string =>
  `${o.FirstName ?? ''} ${o.LastName ?? ''}`.trim() ||
  `Mandant #${o.idLegalClient}`;

/** "1/2" | "0,5" | "" → number | null */
const parseShare = (raw: string): number | null => {
  const v = raw.trim();
  if (v === '') return null;
  if (v.includes('/')) {
    const [n, d] = v.split('/').map(s => Number(s.trim()));
    if (!isNaN(n) && !isNaN(d) && d !== 0) return n / d;
    return null;
  }
  const num = Number(v.replace(',', '.'));
  return isNaN(num) ? null : num;
};

const formatShare = (share?: number | null): string => {
  if (share == null) return '';
  const common: Record<string, string> = {
    '0.5': '1/2',
    '0.25': '1/4',
    '0.75': '3/4',
    '0.3333': '1/3',
    '0.6667': '2/3',
    '1': '1/1',
  };
  const key = share.toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
  return common[key] ?? common[share.toFixed(4)] ?? String(share);
};

/**
 * Eigentümer-Editor (Abteilung I) mit Grundstücks-Referenz: jeder Eigentümer
 * kann optional einem Grundstück (lfd. Nr.) zugeordnet werden; leer = ganzes
 * Blatt. Arbeitet direkt auf ``IBlattOwner[]`` (Index-basiert, damit derselbe
 * Mandant für mehrere Grundstücke eingetragen werden kann).
 */
export const BlattOwnersEditor: React.FC<IProps> = ({
  owners,
  setOwners,
  grundstueckOptions,
}) => {
  // Anteils-Eingabepuffer (Brüche beim Tippen erhalten)
  const [drafts, setDrafts] = useState<Record<number, string>>({});
  const shareValue = (idx: number) =>
    drafts[idx] ?? formatShare(owners[idx]?.share);
  // Ausklapp-Zustand der „1:1"-Detailangaben je Eigentümer
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const toggleExpand = (idx: number) =>
    setExpanded(prev => ({ ...prev, [idx]: !prev[idx] }));

  // Mandanten-Suche
  const [searchInput, setSearchInput] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(searchInput), 350);
    return () => clearTimeout(t);
  }, [searchInput]);
  const searchUri =
    debounced.trim().length >= 2
      ? `search=${['LastName', 'FirstName', 'Email']
          .map(f => `${f}:${debounced.trim()}`)
          .join(';')};&searchtype=includes&page=1&rowsPerPage=20`
      : undefined;
  const { legalClients, isLoadingLegalClients } = useGetLegalClients(searchUri);
  const hits: ILegalClient[] =
    (legalClients as IPagination<ILegalClient[]> | undefined)?.results ?? [];

  const addOwner = (c: ILegalClient) => {
    setOwners([
      ...owners,
      {
        idLegalClient: c.idLegalClient,
        idLegalClientAssetRole: ID_LEGAL_CLIENT_ASSET_ROLE_OWNER,
        share: null,
        idGrundstueck: null,
        FirstName: c.FirstName ?? '',
        LastName: c.LastName ?? '',
        idLegalClientType: c.idLegalClientType ?? 1,
      },
    ]);
    setSearchInput('');
  };

  const updateOwner = (idx: number, patch: Partial<IBlattOwner>) =>
    setOwners(owners.map((o, i) => (i === idx ? { ...o, ...patch } : o)));

  const setShare = (idx: number, raw: string) => {
    setDrafts(prev => ({ ...prev, [idx]: raw }));
    updateOwner(idx, { share: parseShare(raw) });
  };

  const removeOwner = (idx: number) => {
    setOwners(owners.filter((_, i) => i !== idx));
    setDrafts(prev => {
      const next = { ...prev };
      delete next[idx];
      return next;
    });
  };

  const shareSum = owners.reduce((acc, o) => acc + (o.share ?? 0), 0);
  const shareSumWarn =
    owners.some(o => o.share != null) && Math.abs(shareSum - 1) > 0.001;

  return (
    <Box>
      <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 0.5 }}>
        <Typography variant='subtitle2'>Eigentümer (Abteilung I)</Typography>
        <Chip size='small' label={owners.length} variant='outlined' />
      </Stack>

      {owners.length === 0 && (
        <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
          Noch keine Eigentümer erfasst.
        </Typography>
      )}

      <Stack spacing={0.75} sx={{ mb: 1 }}>
        {owners.map((o, idx) => (
          <Paper
            key={`${o.idLegalClient}-${o.idGrundstueck ?? 'blatt'}-${idx}`}
            variant='outlined'
            sx={{ p: 1, opacity: o.istGeloescht ? 0.6 : 1 }}
          >
            <Stack direction='row' spacing={1} alignItems='center'>
              {o.idLegalClientType === 2 ? (
                <BusinessIcon fontSize='small' color='action' />
              ) : (
                <PersonIcon fontSize='small' color='action' />
              )}
              <Typography
                variant='body2'
                sx={{
                  flexGrow: 1,
                  textDecoration: o.istGeloescht ? 'line-through' : 'none',
                }}
              >
                {o.lfdNr ? `${o.lfdNr} · ` : ''}
                {clientName(o)}
              </Typography>
              {o.istGeloescht ? (
                <Chip size='small' label='gelöscht' color='default' />
              ) : null}
              <TextField
                select
                size='small'
                label='Grundstück (lfd. Nr.)'
                value={o.idGrundstueck ?? ''}
                onChange={e =>
                  updateOwner(idx, {
                    idGrundstueck: e.target.value
                      ? Number(e.target.value)
                      : null,
                  })
                }
                sx={{ width: 180 }}
              >
                <MenuItem value=''>— (ganzes Blatt)</MenuItem>
                {grundstueckOptions.map(g => (
                  <MenuItem key={g.id} value={g.id}>
                    Lfd. Nr. {g.lfdNr}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size='small'
                label='Anteil'
                placeholder='z. B. 1/2'
                value={shareValue(idx)}
                onChange={e => setShare(idx, e.target.value)}
                sx={{ width: 110 }}
              />
              <Tooltip title='Grundbuch-Details (Grundlage, Gemeinschaft, …)'>
                <IconButton size='small' onClick={() => toggleExpand(idx)}>
                  <ExpandMore
                    fontSize='small'
                    sx={{
                      transform: expanded[idx] ? 'rotate(180deg)' : 'none',
                      transition: '0.2s',
                    }}
                  />
                </IconButton>
              </Tooltip>
              <Tooltip title='Eigentümer entfernen'>
                <IconButton size='small' onClick={() => removeOwner(idx)}>
                  <DeleteOutline fontSize='small' />
                </IconButton>
              </Tooltip>
            </Stack>

            <Collapse in={!!expanded[idx]} unmountOnExit>
              <Stack spacing={1} sx={{ mt: 1, pl: 4 }}>
                <Stack direction='row' spacing={1} flexWrap='wrap' useFlexGap>
                  <TextField
                    size='small'
                    label='Lfd. Nr. (Eintragung)'
                    placeholder='z. B. 4.1'
                    value={o.lfdNr ?? ''}
                    onChange={e => updateOwner(idx, { lfdNr: e.target.value })}
                    sx={{ width: 150 }}
                  />
                  <TextField
                    size='small'
                    label='Anteil-Text'
                    placeholder='z. B. zu 1/2 Anteil'
                    value={o.AnteilText ?? ''}
                    onChange={e =>
                      updateOwner(idx, { AnteilText: e.target.value })
                    }
                    sx={{ width: 180 }}
                  />
                  <TextField
                    size='small'
                    label='Gemeinschaftsverhältnis'
                    placeholder='Erbengemeinschaft / Bruchteil / …'
                    value={o.Gemeinschaftsverhaeltnis ?? ''}
                    onChange={e =>
                      updateOwner(idx, {
                        Gemeinschaftsverhaeltnis: e.target.value,
                      })
                    }
                    sx={{ flexGrow: 1, minWidth: 220 }}
                  />
                </Stack>
                <TextField
                  size='small'
                  fullWidth
                  multiline
                  label='Grundlage der Eintragung'
                  placeholder='Auflassung / §8 WEG / Zuschlag – Datum, UR-Nr., Notar'
                  value={o.GrundlageDerEintragung ?? ''}
                  onChange={e =>
                    updateOwner(idx, { GrundlageDerEintragung: e.target.value })
                  }
                />
                <Stack
                  direction='row'
                  spacing={1}
                  alignItems='center'
                  flexWrap='wrap'
                  useFlexGap
                >
                  <TextField
                    size='small'
                    type='date'
                    label='Eingetragen am'
                    InputLabelProps={{ shrink: true }}
                    value={o.eingetragenAm ?? ''}
                    onChange={e =>
                      updateOwner(idx, {
                        eingetragenAm: e.target.value || null,
                      })
                    }
                    sx={{ width: 180 }}
                  />
                  <TextField
                    size='small'
                    label='Rechtspfleger'
                    placeholder='Unterzeichner der Eintragung'
                    value={o.Rechtspfleger ?? ''}
                    onChange={e =>
                      updateOwner(idx, { Rechtspfleger: e.target.value })
                    }
                    sx={{ width: 220 }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        size='small'
                        checked={!!o.istGeloescht}
                        onChange={(_, v) =>
                          updateOwner(idx, { istGeloescht: v ? 1 : 0 })
                        }
                      />
                    }
                    label='gelöscht (historisch)'
                  />
                  {o.istGeloescht ? (
                    <TextField
                      size='small'
                      type='date'
                      label='Gelöscht am'
                      InputLabelProps={{ shrink: true }}
                      value={o.geloeschtAm ?? ''}
                      onChange={e =>
                        updateOwner(idx, {
                          geloeschtAm: e.target.value || null,
                        })
                      }
                      sx={{ width: 180 }}
                    />
                  ) : null}
                </Stack>
              </Stack>
            </Collapse>
          </Paper>
        ))}
      </Stack>

      {shareSumWarn && (
        <Alert severity='warning' variant='outlined' sx={{ mb: 1, py: 0 }}>
          Die Summe der Anteile ergibt {formatShare(shareSum)} (≠ 1/1).
        </Alert>
      )}

      <Autocomplete<ILegalClient, false, false, false>
        size='small'
        filterOptions={x => x}
        options={hits}
        loading={isLoadingLegalClients}
        value={null}
        blurOnSelect
        clearOnBlur
        inputValue={searchInput}
        onInputChange={(_e, value, reason) => {
          if (reason === 'reset') return;
          setSearchInput(value);
        }}
        onChange={(_e, value) => {
          if (value) addOwner(value);
        }}
        getOptionLabel={c =>
          `${`${c.FirstName ?? ''} ${c.LastName ?? ''}`.trim()}${
            c.Email ? ` · ${c.Email}` : ''
          }`
        }
        isOptionEqualToValue={(a, b) => a.idLegalClient === b.idLegalClient}
        noOptionsText={
          searchInput.trim().length < 2
            ? 'Mindestens 2 Zeichen eingeben …'
            : 'Keine Treffer'
        }
        renderInput={params => (
          <TextField
            {...params}
            placeholder='Eigentümer suchen (Name, E-Mail) …'
            InputProps={{
              ...params.InputProps,
              startAdornment: <Add fontSize='small' sx={{ mr: 0.5 }} />,
              endAdornment: (
                <>
                  {isLoadingLegalClients ? (
                    <CircularProgress color='inherit' size={16} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Box>
  );
};
